/**
 * iframe-resize — communicate preferred height to the GHL parent frame.
 *
 * Used in compact embeds (ContactWidgetPage, SendFormPanel) to auto-resize
 * the GHL sidebar widget to fit content without scrollbars.
 *
 * GHL parent must listen for the postMessage event:
 *   window.addEventListener('message', (e) => {
 *     if (e.data?.type === 'clearintake:resize') { ... }
 *   })
 */

/**
 * Tell the GHL parent frame our preferred height.
 * No-op when not running inside an iframe (e.g., full staff app).
 */
export function notifyParentHeight(height: number): void {
  if (window.parent !== window) {
    window.parent.postMessage({ type: 'clearintake:resize', height }, '*');
  }
}

/**
 * Auto-detect the document's scroll height and notify the parent.
 * Useful when you don't want to manually calculate the height.
 */
export function autoResizeParent(): void {
  notifyParentHeight(document.documentElement.scrollHeight);
}

/**
 * Set up a ResizeObserver that continuously keeps the parent frame in sync.
 * Returns a cleanup function — call it in a useEffect cleanup.
 *
 * Usage:
 *   useEffect(() => {
 *     return observeAndResize(containerRef.current)
 *   }, [])
 */
export function observeAndResize(element: HTMLElement | null): () => void {
  if (!element || window.parent === window) return () => { /* no-op */ };

  const observer = new ResizeObserver((entries) => {
    const entry = entries[0];
    if (entry) {
      notifyParentHeight(Math.ceil(entry.contentRect.height) + 32); // +32 for padding
    }
  });

  observer.observe(element);
  return () => observer.disconnect();
}
